import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Alert, CircularProgress,
} from '@mui/material';
import api from '../services/api';

/**
 * Shared dialog for the three physical-delivery lifecycle marks:
 *   delivered — carrier POD signature (signed_for_by_name +
 *               delivered_at + auto status → DELIVERED)
 *   received  — venue confirms file is readable (received_by_name
 *               + received_at)
 *   tested    — venue ran the DCP through a projector
 *               (tested_by_name + test_notes + tested_at)
 *
 * Pre-v10.18.20 this lived inline in operator/PhysicalDrives.jsx.
 * Hoisted out so the DCP Distribution recipient card can reuse the
 * same dialog without duplicating ~80 lines of JSX + API plumbing.
 *
 * v10.18.21: the relevant TIMESTAMP is now a required form field
 * with default "now". The actual moment the venue received / the
 * booth tested is usually NOT when the operator gets around to
 * logging it — operators log hours or days after the fact based
 * on email or a phone call. Auto-now hides that real timestamp
 * behind whenever-the-operator-clicked-save.
 *
 * Props:
 *   open            boolean — dialog visibility
 *   onClose         () => void — fired on Cancel or after successful save
 *   onSaved         (kind, delivery_id) => void — fired after successful PATCH
 *   kind            'delivered' | 'received' | 'tested'
 *   delivery        the physical_delivery row (id + venue_name + ship_to_name)
 *
 * Backend: PATCH /api/physical-delivery/deliveries/{id}
 *   accepts received_at/tested_at/delivered_at directly; when omitted
 *   the backend auto-stamps now() but we send the operator-entered
 *   value explicitly so the audit trail reflects the real event time.
 */

// "YYYY-MM-DDTHH:MM" suitable for <input type="datetime-local">. The
// browser interprets this as local time; on submit we round-trip it
// through Date so the API receives a timezone-bearing ISO string.
function _pad(n) { return String(n).padStart(2, '0'); }

function nowAsDateTimeLocal() {
  return isoToDateTimeLocal(new Date().toISOString()) || '';
}

function isoToDateTimeLocal(iso) {
  // Backend sends ISO with timezone; convert to local-time string
  // the datetime-local input understands.
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
}

function dateTimeLocalToIso(s) {
  if (!s) return null;
  // datetime-local has no timezone; treat as local time and convert
  // to ISO with the browser's offset baked in.
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function PhysicalDeliveryMarkDialog({ open, onClose, onSaved, kind, delivery }) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [eventAt, setEventAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // On open, prefill from existing row values if the mark already
  // exists (operator is editing) — otherwise blank name + "now"
  // timestamp for a fresh mark. v10.18.22 added the edit path so
  // operators who marked received/tested before the timestamp field
  // existed can correct the auto-stamped now() to the real moment.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (kind === 'delivered') {
      setName(delivery?.signed_for_by_name || '');
      setEventAt(isoToDateTimeLocal(delivery?.delivered_at) || nowAsDateTimeLocal());
      setNotes('');
    } else if (kind === 'received') {
      setName(delivery?.received_by_name || '');
      setEventAt(isoToDateTimeLocal(delivery?.received_at) || nowAsDateTimeLocal());
      setNotes('');
    } else if (kind === 'tested') {
      setName(delivery?.tested_by_name || '');
      setEventAt(isoToDateTimeLocal(delivery?.tested_at) || nowAsDateTimeLocal());
      setNotes(delivery?.test_notes || '');
    } else {
      setName('');
      setNotes('');
      setEventAt(nowAsDateTimeLocal());
    }
  }, [open, kind, delivery]);

  // True when there's already an existing mark for this kind. Drives
  // dialog title prefix ("Edit ..." vs "Mark ...") and disabled-Save
  // logic.
  const isEditing = !!(
    delivery && (
      (kind === 'delivered' && delivery.delivered_at)
      || (kind === 'received' && delivery.received_at)
      || (kind === 'tested' && delivery.tested_at)
    )
  );

  const verb = isEditing ? 'Edit' : 'Mark';
  const title = kind === 'delivered' ? `${verb} delivered (carrier POD)`
    : kind === 'received' ? `${verb} received by venue`
    : kind === 'tested' ? `${verb} tested at venue`
    : verb;

  const nameLabel = kind === 'delivered' ? 'Signed for by (POD name)'
    : kind === 'received' ? 'Received by (name)'
    : 'Tested by (name)';

  const namePlaceholder = kind === 'delivered'
    ? 'e.g. J. Smith (from FedEx POD)'
    : kind === 'received' ? 'e.g. Christine Huerta'
    : 'e.g. booth tech';

  const dateLabel = kind === 'delivered' ? 'Delivered at'
    : kind === 'received' ? 'Received at'
    : 'Tested at';

  const onSave = async () => {
    if (!delivery) return;
    setBusy(true);
    setError(null);
    try {
      const eventIso = dateTimeLocalToIso(eventAt);
      let payload;
      if (kind === 'delivered') {
        payload = {
          signed_for_by_name: name || 'unspecified',
          ...(eventIso ? { delivered_at: eventIso } : {}),
        };
      } else if (kind === 'received') {
        payload = {
          received_by_name: name || 'venue',
          ...(eventIso ? { received_at: eventIso } : {}),
        };
      } else {
        payload = {
          tested_by_name: name || 'venue',
          test_notes: notes || null,
          ...(eventIso ? { tested_at: eventIso } : {}),
        };
      }
      await api.patch(`/api/physical-delivery/deliveries/${delivery.id}`, payload);
      if (onSaved) onSaved(kind, delivery.id);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {delivery && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Delivery #{delivery.id}
            {delivery.venue_name ? ` · ${delivery.venue_name}` : ''}
            {delivery.ship_to_name ? ` · ${delivery.ship_to_name}` : ''}
          </Typography>
        )}
        <TextField fullWidth margin="dense" size="small" autoFocus
                   label={nameLabel}
                   value={name} onChange={(e) => setName(e.target.value)}
                   placeholder={namePlaceholder} />
        <TextField fullWidth margin="dense" size="small"
                   type="datetime-local"
                   label={dateLabel}
                   value={eventAt}
                   onChange={(e) => setEventAt(e.target.value)}
                   InputLabelProps={{ shrink: true }}
                   helperText="When the event actually happened (not when you logged it)" />
        {kind === 'tested' && (
          <TextField fullWidth margin="dense" size="small" multiline minRows={3}
                     label="Test notes (optional)"
                     value={notes} onChange={(e) => setNotes(e.target.value)}
                     placeholder="Played clean on the SR-1000 / no audio issues / etc." />
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !delivery} onClick={onSave}>
          {busy ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
